import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CloudArrowUp,
  Lightning,
  ShieldCheck,
  ArrowsOutCardinal,
  Cpu,
  Stack,
  ArrowUpRight,
  Check,
} from "@phosphor-icons/react";

const services = [
  {
    icon: CloudArrowUp,
    title: "Cloud Orchestration",
    tagline: "Multi-region by default.",
    desc: "Automated deployment pipelines, zero-downtime rollouts, and global load distribution across every region you serve. Built on Kubernetes-native primitives with policy-as-code guardrails.",
    bullets: [
      "Blue/green and canary rollouts",
      "Multi-cluster federation",
      "Region-aware failover (RTO < 30s)",
      "Cost-aware autoscaling",
    ],
  },
  {
    icon: Lightning,
    title: "DevOps Automation",
    tagline: "Velocity without entropy.",
    desc: "Continuous integration and delivery frameworks tailored for engineering velocity. We replace bespoke YAML graveyards with declarative, opinionated pipelines that scale with your org.",
    bullets: [
      "Declarative CI/CD pipelines",
      "Ephemeral preview environments",
      "Hermetic builds and SBOM signing",
      "Progressive delivery with auto-rollback",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Zero-Trust Security",
    tagline: "Default-deny. Always.",
    desc: "Military-grade encryption, granular access control, and policy enforced at the edge. Every request authenticated, every workload identity-bound, every secret short-lived and audited.",
    bullets: [
      "Workload identity & mTLS everywhere",
      "Hardware-backed key management",
      "SOC 2 Type II, ISO 27001, HIPAA",
      "Real-time anomaly detection",
    ],
  },
  {
    icon: ArrowsOutCardinal,
    title: "Elastic Scaling",
    tagline: "Predictive, not reactive.",
    desc: "Infrastructure that expands predictively as your traffic spikes — never reactively. Our models forecast load 90 seconds in advance and provision capacity before queues form.",
    bullets: [
      "Predictive autoscaling (ML-driven)",
      "Cold-start elimination at the edge",
      "Per-tenant capacity isolation",
      "Spot + reserved blended capacity",
    ],
  },
  {
    icon: Cpu,
    title: "Observability",
    tagline: "See every packet.",
    desc: "Unified telemetry across metrics, logs, traces, and profiles. Open standards (OpenTelemetry, Prometheus) with proprietary query speed that makes p99.9 debugging actually possible.",
    bullets: [
      "OpenTelemetry-native ingest",
      "Continuous profiling",
      "Sub-second log search at PB scale",
      "Service-level objectives as code",
    ],
  },
  {
    icon: Stack,
    title: "Platform Engineering",
    tagline: "An internal developer platform.",
    desc: "A golden-path platform your engineers actually want to use. Self-service environments, paved roads for common workloads, and a backstage portal that doesn't get abandoned in month three.",
    bullets: [
      "Self-service environment provisioning",
      "Paved-road templates",
      "Backstage / developer portal",
      "Cost attribution by service",
    ],
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="eyebrow mb-6">Capabilities · 06</div>
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight"
            >
              An infrastructure
              <br />
              layer for{" "}
              <span className="text-blue-700">teams that ship.</span>
            </motion.h1>
            <p className="mt-8 text-lg text-zinc-600 max-w-2xl leading-relaxed">
              Six tightly integrated capabilities. One operational surface.
              Use what you need — replace whatever you&apos;ve outgrown.
            </p>
          </div>
          <div className="lg:col-span-4 lg:border-l lg:border-zinc-200 lg:pl-10 flex flex-col justify-end">
            <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-500">
              Engagement model
            </div>
            <p className="mt-3 text-zinc-700 leading-relaxed">
              Senior architects partner directly with your team — no junior
              handoffs, no opaque ticket queues, no offshore relay.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-[1400px]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-zinc-200">
            {services.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={`service-detail-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="border-r border-b border-zinc-200 p-8 lg:p-10"
                >
                  <Icon
                    size={28}
                    weight="duotone"
                    className="text-blue-700"
                  />
                  <h3 className="mt-6 font-display text-2xl tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-2 font-mono-tech text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                    {s.tagline}
                  </p>
                  <p className="mt-4 text-zinc-600 leading-relaxed text-[15px]">
                    {s.desc}
                  </p>
                  <ul className="mt-6 space-y-2.5">
                    {s.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-2.5 text-sm text-zinc-700"
                      >
                        <Check
                          size={14}
                          weight="bold"
                          className="mt-1 text-blue-700 flex-shrink-0"
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
          <div className="eyebrow mb-6">How we work</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-3xl">
            Architecture assessment in four steps.
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-t border-zinc-200">
            {[
              { n: "01", t: "Discovery", d: "A 60-minute architecture review. We listen first." },
              { n: "02", t: "Blueprint", d: "A written, signed-off design with explicit trade-offs." },
              { n: "03", t: "Deploy", d: "Senior engineers ship the first production slice in week one." },
              { n: "04", t: "Operate", d: "24/7 on-call, monthly architecture reviews, no surprises." },
            ].map((step) => (
              <div
                key={step.n}
                className="border-r border-b border-zinc-200 p-8 lg:p-10"
              >
                <div className="font-mono-tech text-xs text-blue-700 tracking-[0.2em]">
                  {step.n}
                </div>
                <div className="mt-4 font-display text-2xl">{step.t}</div>
                <p className="mt-3 text-zinc-600 text-sm leading-relaxed">
                  {step.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-zinc-950 text-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-24 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-2xl">
            Book a senior-architect assessment.
          </h2>
          <Link
            to="/contact"
            data-testid="services-cta"
            className="inline-flex items-center gap-2 bg-blue-700 text-white px-7 py-4 text-base font-medium hover:bg-blue-600 transition-colors w-fit"
          >
            Start your assessment
            <ArrowUpRight size={18} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}
